import { Box, Stack, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { homeAction } from "redux/slice/home";
import { calculateKpiPoints } from "utils/HelperFunctions";
import { Bar } from "react-chartjs-2";
import { enqueueSnackbar } from "notistack";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import MDButton from "components/MDButton";
import {
  generateColorFromString,
  hexToRgba,
} from "utils/GenerateColorFromString/GenerateColorFromString";
// import FunnelTierOptions from "./FunnelTierOptions";

function UserKpiInput({ kpi, handleActionEdit }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const kpiCount = kpi?.DailyActions?.[0]?.count || 0;
  const display = {
    count: kpi.dailyGoal || kpi.dailyAvg,
    type: kpi.dailyGoal ? "goal" : "avg",
  };
  const hexColor = generateColorFromString(kpi.KpiActivity.name);
  const data = {
    labels: [""],
    datasets: [
      {
        label: "count",
        backgroundColor: hexColor,
        hoverBackgroundColor: hexColor,
        data: [kpiCount],
        borderRadius: 100,
        borderSkipped: false,
      },
      {
        label: "target",
        backgroundColor: kpi.dailyGoal ? hexToRgba(hexColor, 0.2) : "#fafafa",
        hoverBackgroundColor: kpi.dailyGoal ? hexToRgba(hexColor, 0.2) : "#fafafa",
        data: [Math.max(display.count, 1)],
        borderRadius: 100,
        borderSkipped: false,
      },
    ],
  };
  const options = {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: "y",
    animation: false,
    scales: {
      x: {
        display: false,
        stacked: false,
        min: 0,
        max: Math.max(kpiCount, display.count, 1),
      },
      y: {
        display: false,
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    <Stack direction="row" alignItems="flex-end" width="100%">
      <MDButton
        disabled={kpi.isKpiLocked || kpi.disabled}
        sx={{
          cursor: "pointer",
          minWidth: "unset",
          minHeight: "unset",
          width: "0.5rem",
          height: "2rem",
          "&:hover": {
            background: `${theme.palette.trendRed.main}10`,
          },
          mr: "0.15rem",
          mb: "-0.15rem",
        }}
        onClick={() => {
          const dailyActionCount = kpi?.DailyActions[0]?.count;
          if (typeof dailyActionCount === "number" && dailyActionCount - 1 >= 0) {
            handleActionEdit({
              kpiActionId: kpi.kpiActionId,
              count: dailyActionCount - 1,
              points: calculateKpiPoints(kpi.count, kpi.points, dailyActionCount - 1),
              disabled: true,
              call: true,
              action: "decrement",
              daily: true,
            });
          }
        }}
      >
        <RemoveCircleOutline
          sx={{
            color: kpi.isKpiLocked ? theme.palette.gray[20] : theme.palette.trendRed.main,
            width: "18px",
            height: "18px",
          }}
        />
      </MDButton>
      <Stack direction="column" width="100%" height="100%">
        <Stack direction="row" justifyContent="space-between">
          <Box width="50px" />
          <Stack direction="row" alignItems="center">
            <TextField
              type="number"
              value={kpi?.DailyActions?.[0]?.count?.toString()}
              inputProps={{ style: { textAlign: "center" } }}
              onMouseEnter={(e) =>
                e.target.addEventListener("wheel", (event) => {
                  event.preventDefault();
                })
              }
              disabled={kpi.disabled || kpi.isKpiLocked}
              sx={{
                width: "75px",
                // backgroundColor: "#f3f3f3",
                backgroundColor: "#fff",
                borderRadius: "0.5rem",
                "& input[type=number]": {
                  MozAppearance: "textfield",
                  height: ".8em",
                  fontSize: "1.4rem",
                },
                "& input[type=number]::-webkit-outer-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                  fontSize: "1.4rem",
                },
                "& input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                  fontSize: "1.4rem",
                },
                "& fieldset": { border: "none" },
                "& .MuiTextField-root": { ml: 0 },
                "& .MuiOutlinedInput-input": { p: "0 0.4rem", borderRadius: "0.5rem" },
                "& .MuiOutlinedInput-input:hover": { color: "#888" },
                // "& :hover": { color: "blue" },
              }}
              onChange={(event) => {
                if (event.target.value < 0) return;
                if (parseInt(event.target.value || 0, 10) > 999) {
                  dispatch(
                    homeAction.updateKpiEntrySaga({
                      updatedActionData: {
                        kpiActionId: kpi.kpiActionId,
                        count: 999,
                        points:
                          kpi.count > kpi.points
                            ? parseInt(999 / kpi.count, 10)
                            : parseInt(999 * kpi.points, 10),
                        call: false,
                      },
                    })
                  );
                }
                if (parseInt(event.target.value || 0, 10) <= 999) {
                  dispatch(
                    homeAction.updateKpiEntrySaga({
                      updatedActionData: {
                        kpiActionId: kpi.kpiActionId,
                        count: parseInt(event.target.value || 0, 10),
                        points: calculateKpiPoints(
                          kpi.count,
                          kpi.points,
                          parseInt(event.target.value || 0, 10)
                        ),
                        call: false,
                      },
                    })
                  );
                }
              }}
              onBlur={(event) => {
                if (
                  parseInt(event.target.value || 0, 10) !==
                  (typeof kpi?.prevCount !== "undefined"
                    ? kpi?.prevCount
                    : kpi?.DailyActions[0]?.count)
                ) {
                  handleActionEdit({
                    kpiActionId: kpi.kpiActionId,
                    count: parseInt(event.target.value || 0, 10),
                    points: calculateKpiPoints(kpi.count, kpi.points, kpi?.DailyActions[0]?.count),
                    disabled: true,
                    call: true,
                    action: "change",
                    daily: true,
                    oldCount:
                      typeof kpi?.prevCount !== "undefined"
                        ? kpi?.prevCount
                        : kpi?.DailyActions[0]?.count,
                  });
                }
              }}
            />
          </Stack>
          <Box
            display="flex"
            width="50px"
            alignItems="flex-end"
            justifyContent="flex-end"
            pr="0.5rem"
          >
            <Typography sx={{ fontStyle: "italic", color: "#888", fontSize: "0.9rem" }}>
              {display.count === 0 ? "<1" : display.count}
            </Typography>
            <Typography
              sx={{ fontStyle: "italic", color: "#888", fontSize: "0.8rem", pl: "0.2rem" }}
            >
              {display.type}
            </Typography>
          </Box>
        </Stack>
        <Box height="25px" width="99%">
          <Bar options={options} data={data} />
        </Box>
      </Stack>
      <MDButton
        disabled={kpi.isKpiLocked || kpi.disabled}
        sx={{
          cursor: "pointer",
          minHeight: "unset",
          minWidth: "unset",
          width: "1rem",
          height: "2rem",
          mb: "-0.15rem",
          "&:hover": {
            background: `${theme.palette.trendGreen.main}10`,
          },
        }}
        onClick={() => {
          const dailyActionCount = kpi?.DailyActions[0]?.count;
          if (typeof dailyActionCount === "number") {
            if (dailyActionCount + 1 > 999) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: "Maximum 3 Digits Number Are Allowed.",
                }),
                {
                  variant: "custom",
                }
              );
            } else {
              handleActionEdit({
                kpiActionId: kpi.kpiActionId,
                count: dailyActionCount + 1,
                points: calculateKpiPoints(kpi.count, kpi.points, dailyActionCount + 1),
                daily: true,
                disabled: true,
                call: true,
                action: "increment",
                requiredFields: kpi?.KpiActivity?.requiredFields,
              });
            }
          }
        }}
      >
        <AddCircleOutline
          sx={{
            color: kpi.isKpiLocked ? theme.palette.gray[20] : theme.palette.trendGreen.main,
            width: "18px",
            height: "18px",
          }}
        />
      </MDButton>
    </Stack>
  );
}

export default UserKpiInput;
